/**
 * Runtime leak patch for v-tooltip@2.1.2 (popper.js@1) on Vue 2 teardown.
 *
 * Root cause (verified against node_modules/v-tooltip/dist/v-tooltip.esm.js):
 *
 *   Tooltip#_dispose() calls this._hide(), and _hide() (re)arms a 5000ms
 *   `_disposeTimer` (= directive.options.disposeTimeout). _dispose() never clears
 *   that timer. So after the directive's `unbind` runs (slide switch, component
 *   teardown), the setTimeout closure keeps the whole Tooltip instance alive —
 *   and with it `tooltip.reference`, the detached trigger DOM element — for a full
 *   5 seconds. Under rapid editor slide-switching, teardowns outpace the 5s expiry
 *   and detached DOM accumulates on the heap.
 *
 *   <VPopover> has the identical shape: dispose() -> hide() schedules `$_scheduleTimer`
 *   and `$_hide()` arms a 5000ms `$_disposeTimer`, neither cleared by dispose(). The
 *   armed timer pins the entire destroyed Vue component instance (and its subtree)
 *   for 5s.
 *
 * Fix: wrap the directive's `unbind` and the component's `dispose` to clear those
 * dangling timers immediately after the library's own teardown. This is strictly
 * behaviour-preserving — the timers only ever fire AFTER teardown, and post-dispose
 * their own guards already make them no-ops (the nodes they reference are gone).
 * We are only releasing the closures earlier so GC can reclaim the detached DOM.
 *
 * Patches the exact objects the library registered: `VTooltip` (the directive,
 * === the object passed to Vue.directive('tooltip', ...)) and `VPopover` (the
 * component options object, === Vue.component('VPopover', ...)). Must run AFTER
 * `Vue.use(VTooltip, ...)`.
 */
import { VTooltip, VPopover } from 'v-tooltip'

let patched = false

export default function applyVTooltipLeakPatch(): void {
  if (patched) return
  patched = true

  // --- Directive: `v-tooltip="..."` (the dominant path, 200+ usages) ---
  const directive = VTooltip as any
  if (directive && typeof directive.unbind === 'function') {
    const originalUnbind = directive.unbind
    directive.unbind = function patchedUnbind(el: any, ...rest: any[]): void {
      // Capture the instance before destroyTooltip() deletes el._tooltip.
      const tooltip = el && el._tooltip
      originalUnbind.call(this, el, ...rest)
      if (tooltip) {
        clearTimeout(tooltip._disposeTimer)
        clearTimeout(tooltip._scheduleTimer)
        tooltip._disposeTimer = null
        tooltip._scheduleTimer = null
      }
    }
  }

  // --- Component: `<v-popover>` / `<VPopover>` ---
  const popover = VPopover as any
  const methods = popover && popover.options ? popover.options.methods : popover && popover.methods
  if (methods && typeof methods.dispose === 'function') {
    const originalDispose = methods.dispose
    methods.dispose = function patchedDispose(this: any, ...args: any[]): void {
      originalDispose.apply(this, args)
      clearTimeout(this.$_disposeTimer)
      clearTimeout(this.$_scheduleTimer)
      this.$_disposeTimer = null
      this.$_scheduleTimer = null
    }
  }
}
